import { useCallback, useMemo } from 'react'

import { getPathWithoutLocale } from '../_utils/getPathWithoutLocale'
import { routeHashMatchesLinkFragment } from '../_utils/isNavPathActive'
import { useHeaderPathname } from '../_utils/useHeaderPathname'
import { normalizeRouteHashFragment, useHeaderRouteHash } from '../_utils/useHeaderRouteHash'

import type { ILink } from './useHeaderLinks'

function isSubtreeLinkActive(link: ILink, isLinkActive: (l: ILink | string) => boolean): boolean {
	if (isLinkActive(link)) return true
	return link.children?.some(child => isSubtreeLinkActive(child, isLinkActive)) ?? false
}

function splitLinkPathAndHash(linkPath: string): {
	pathPart: string
	hashFragment: string | null
} {
	const hashIdx = linkPath.indexOf('#')
	if (hashIdx === -1) {
		return { pathPart: linkPath, hashFragment: null }
	}
	return {
		pathPart: linkPath.slice(0, hashIdx),
		hashFragment: normalizeRouteHashFragment(linkPath.slice(hashIdx + 1))
	}
}

export const useHeaderNavigation = () => {
	const pathname = useHeaderPathname()
	const routeHash = useHeaderRouteHash()

	const pathWithoutLocale = useMemo(() => getPathWithoutLocale(pathname), [pathname])

	const isLinkActive = useCallback(
		(linkOrPath: ILink | string): boolean => {
			const linkPath = typeof linkOrPath === 'string' ? linkOrPath : linkOrPath.to
			const exactActive =
				typeof linkOrPath === 'string' ? false : linkOrPath.exactActive === true
			const excludeNext =
				typeof linkOrPath === 'string' ? undefined : linkOrPath.activeExcludeNextSegments

			const { pathPart, hashFragment: linkHashFragment } = splitLinkPathAndHash(linkPath)
			const linkPathNorm = pathPart.replace(/^\//, '').split(/[?#]/)[0]
			const pathNorm = pathWithoutLocale.replace(/^\//, '').split(/[?#]/)[0]
			if (!pathNorm) return linkPathNorm === '' || linkPath === '/'
			if (!linkPathNorm) return pathNorm === ''

			if (pathNorm === linkPathNorm) {
				if (linkHashFragment !== null) {
					return routeHashMatchesLinkFragment(routeHash, linkHashFragment)
				}
				if (exactActive) {
					return routeHash === ''
				}
				return true
			}

			/** Hub section rows (`/hub#anchor`) stay inactive on program detail URLs; only leaves match. */
			if (linkHashFragment !== null) {
				return false
			}

			if (exactActive) return false
			if (!pathNorm.startsWith(linkPathNorm + '/')) return false
			if (excludeNext?.length) {
				const rest = pathNorm.slice(linkPathNorm.length + 1).split('/')[0]
				if (rest && excludeNext.includes(rest)) return false
			}
			return true
		},
		[pathWithoutLocale, routeHash]
	)

	const isParentLinkActive = useCallback(
		(linkPath: string, hasChildren: boolean, children?: ILink[]): boolean => {
			if (hasChildren && children?.length) {
				return children.some(child => isSubtreeLinkActive(child, isLinkActive))
			}
			return isLinkActive(linkPath)
		},
		[isLinkActive]
	)

	return {
		isLinkActive,
		isParentLinkActive
	}
}
